'use client';

/* eslint-disable react/no-danger -- Bundle Card renders CMS rich text for CMS-1627. */

import { RichText } from '@hyphengroup/cms-components/components/RichText';
import { CmsOffer } from '@hyphengroup/cms-components/types/cmsProduct';
import classNames from 'classnames';
import { MaterialIcon } from 'components/modulab/MaterialIcon';
import { KeyboardEvent, MouseEvent, useEffect, useState } from 'react';
import { ApplyStaticText } from '$/(cms)/-cms/apply-v3/[[...slugs]]/i18n';
import {
  getBundleOfferId,
  getBundleRewardLabel,
} from '$/(cms)/-cms/apply-v3/[[...slugs]]/lib/helpers';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';

type Props = {
  expanded: boolean;
  offer: CmsOffer;
  position: number;
  selected: boolean;
  onSelect: (offer: CmsOffer, position: number) => void;
  onToggleExpanded: (offer: CmsOffer, position: number) => void;
};

const copiedFeedbackMs = 1500;

export function BundleCard({
  expanded,
  offer,
  position,
  selected,
  onSelect,
  onToggleExpanded,
}: Props) {
  const context = useClientContext();
  const staticText = getClientTranslations(ApplyStaticText, context);
  const [copied, setCopied] = useState(false);
  const cardId = getBundleOfferId(offer);
  const rewardLabel = getBundleRewardLabel(offer);
  const promoCode = offer.promo_code?.trim();
  const badge = offer.badge_tag?.trim();
  const isHighlighted = selected;

  useEffect(() => {
    if (!copied) return;
    const timeout = window.setTimeout(() => setCopied(false), copiedFeedbackMs);
    return () => window.clearTimeout(timeout);
  }, [copied]);

  const selectCard = () => onSelect(offer, position);

  const handleCardKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    selectCard();
  };

  const handleToggleDetails = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onToggleExpanded(offer, position);
  };

  const handleCopyPromoCode = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!promoCode) return;

    try {
      await navigator.clipboard.writeText(promoCode);
      setCopied(true);
    } catch (error) {
      console.error('Failed to copy bundle promo code:', error);
    }
  };

  return (
    <div
      aria-pressed={selected}
      className={classNames(
        'w-full cursor-pointer overflow-hidden rounded-radius/card border-2 border-borders/default transition-shadow bg-fill/card',
        {
          'shadow-md bg-gradient-to-r from-highlights/green-light/50 to-fill/card':
            isHighlighted,
        },
      )}
      onClick={selectCard}
      onKeyDown={handleCardKeyDown}
      role="button"
      tabIndex={0}
    >
      <div className="flex flex-col gap-spacing/gap/y-sm px-spacing/gap/x py-spacing/gap/y md:gap-spacing/gap/y md:px-5 md:py-6 lg:px-6">
        {badge ? (
          <div className="flex items-center justify-between gap-spacing/gap/x-sm">
            <span
              className="mhg-alpha-typography-badge inline-flex h-tag/size items-center justify-center rounded-radius/button px-tag/padding-x bg-highlights/orange-dark text-text/on-dark"
              style={{ backgroundColor: offer.badge_color || undefined }}
            >
              {badge}
            </span>
            <button
              aria-controls={`bundle-card-details-${cardId}`}
              aria-expanded={expanded}
              aria-label={
                expanded ? staticText.seeLess : staticText.seeFullDetails
              }
              className="flex size-5 shrink-0 items-center justify-center text-buttons/neutral md:size-6"
              onClick={handleToggleDetails}
              type="button"
            >
              <MaterialIcon
                className={classNames('transition-transform', {
                  'rotate-90': expanded,
                })}
                icon="chevron_right"
                px={24}
              />
            </button>
          </div>
        ) : null}

        <div className="flex items-start justify-between gap-spacing/gap/x-sm">
          <div className="min-w-0 flex-1">
            <h3 className="mhg-alpha-typography-title-2 md:mhg-alpha-typography-subheading-2 text-text/primary">
              {offer.offer_title}
            </h3>
            <div className="mhg-alpha-typography-body-2-relaxed mt-spacing/gap/soft-4px text-text/secondary-lighter">
              <RichText>{offer.introduction}</RichText>
            </div>
            {rewardLabel ? (
              <p className="mhg-alpha-typography-caption mt-spacing/padding/soft-8px text-text/primary">
                {rewardLabel}
              </p>
            ) : null}
          </div>
          {!badge ? (
            <button
              aria-controls={`bundle-card-details-${cardId}`}
              aria-expanded={expanded}
              aria-label={
                expanded ? staticText.seeLess : staticText.seeFullDetails
              }
              className="flex size-5 shrink-0 items-center justify-center text-buttons/neutral md:size-6"
              onClick={handleToggleDetails}
              type="button"
            >
              <MaterialIcon
                className={classNames('transition-transform', {
                  'rotate-90': expanded,
                })}
                icon="chevron_right"
                px={24}
              />
            </button>
          ) : null}
        </div>

        {promoCode ? (
          <div className="relative flex items-center gap-spacing/gap/soft-4px">
            <span className="mhg-alpha-typography-body-2-relaxed text-text/secondary-lighter">
              {staticText.promoCode}
            </span>
            <span className="mhg-alpha-typography-bullet-2 text-fill/primary">
              {promoCode}
            </span>
            <button
              aria-label={
                copied ? staticText.selectedPromoCode : staticText.copyPromoCode
              }
              className="flex size-4 items-center justify-center text-buttons/neutral"
              onClick={handleCopyPromoCode}
              type="button"
            >
              <MaterialIcon icon={copied ? 'check' : 'content_copy'} px={16} />
            </button>
            {copied ? (
              <span className="mhg-alpha-typography-caption absolute left-0 top-full mt-spacing/gap/soft-4px rounded-radius/tooltip px-spacing/padding/soft-8px py-spacing/padding/soft-4px shadow bg-fill/card text-text/secondary-lighter">
                {staticText.copied}
              </span>
            ) : null}
          </div>
        ) : null}

        {expanded && offer.description ? (
          <div
            className="cms-rich-text mhg-alpha-typography-caption rounded-radius/card-inner p-spacing/padding/y-sm bg-fill/alternative-1 text-text/secondary-lighter [&_ol]:list-decimal [&_ol]:pl-5 [&_p:last-child]:mb-0 [&_p]:mb-spacing/gap/soft-12px [&_ul]:list-disc [&_ul]:pl-5"
            dangerouslySetInnerHTML={{ __html: offer.description }}
            id={`bundle-card-details-${cardId}`}
          />
        ) : null}
      </div>

      {!expanded ? (
        <button
          aria-controls={`bundle-card-details-${cardId}`}
          aria-expanded={expanded}
          className="mhg-alpha-typography-button-2 flex w-full items-center gap-spacing/gap/soft-4px border-t border-borders/default px-spacing/padding/x py-spacing/padding/y-sm text-left text-buttons/neutral lg:px-6"
          onClick={handleToggleDetails}
          type="button"
        >
          <MaterialIcon icon="article" px={20} />
          {staticText.seeFullDetails}
        </button>
      ) : null}
    </div>
  );
}
